import { useCallback, useRef, useState } from 'react'

type FieldOption = {
  value: string
  label: string
}

type InterviewField = {
  key: string
  label: string
  type: 'text' | 'number' | 'textarea' | 'radio' | 'checkbox_group' | 'checkbox' | 'date' | 'file'
  required: boolean
  options?: FieldOption[]
  description?: string
  conditions?: {
    sex?: string
  }
  depends_on?: string
  placeholder?: string
}

type Props = {
  fields: InterviewField[]
  values: Record<string, string | string[]>
  errors: Record<string, string[]>
  onChange: (key: string, value: string | string[]) => void
  shouldShowField: (field: InterviewField) => boolean
  provisionalReservationToken?: string
}

const UNIT_BY_FIELD_KEY: Record<string, string> = {
  height: 'cm',
  weight: 'kg',
  basal_body_temperature: '℃',
  blood_pressure_upper: 'mmHg',
  blood_pressure_lower: 'mmHg',
  smoking_past_age_from: '歳',
  smoking_past_age_to: '歳',
  smoking_present_age_from: '歳',
  smoking_past_number: '本',
  smoking_present_number: '本',
  latest_menstruation_term: '日数',
}

export default function InterviewForm({ fields, values, errors, onChange, shouldShowField, provisionalReservationToken }: Props) {
  const visibleFields = fields.filter((field) => shouldShowField(field))

  const renderField = useCallback(
    (field: InterviewField, index: number) => {
      const fieldError = errors[field.key]
      const hasError = fieldError && fieldError.length > 0

      return (
        <div
          key={field.key}
          className={`mb-6 ${index < visibleFields.length - 1 ? 'border-b border-[#d4d6d7] pb-6' : ''}`}
        >
          <div className="mb-2">
            <div className="oc-field-title">
              <span>■</span>
              <label>{field.label}</label>
              {field.required ? (
                <span className="oc-badge-required">必須</span>
              ) : (
                <span className="oc-badge-optional">任意</span>
              )}
            </div>
          </div>

          {field.description && <p className="mb-3 text-sm text-[#6f7680]">{field.description}</p>}

          {field.type === 'file' ? (
            <FileUploadInput
              fieldKey={field.key}
              value={(values[field.key] as string) || ''}
              onChange={onChange}
              provisionalReservationToken={provisionalReservationToken}
              hasError={hasError}
            />
          ) : (
            renderFieldInput(field, values, onChange, hasError)
          )}

          {hasError && (
            <p className="mt-2 text-sm text-red-600">
              {fieldError.map((err, i) => (
                <span key={i}>{err}</span>
              ))}
            </p>
          )}
        </div>
      )
    },
    [errors, onChange, values, visibleFields.length, provisionalReservationToken]
  )

  return <div>{visibleFields.map(renderField)}</div>
}

// ファイルアップロード用のコンポーネント（hooks使用のため分離）
function FileUploadInput({
  fieldKey,
  value,
  onChange,
  provisionalReservationToken,
  hasError,
}: {
  fieldKey: string
  value: string
  onChange: (key: string, value: string | string[]) => void
  provisionalReservationToken?: string
  hasError: boolean
}) {
  const [isUploading, setIsUploading] = useState(false)
  const [preview, setPreview] = useState<string | null>(value || null)
  const [filename, setFilename] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      setUploadError(null)

      const resetSelection = () => {
        setPreview(null)
        setFilename(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }

      if (file.size > 10 * 1024 * 1024) {
        const actualMb = (file.size / 1024 / 1024).toFixed(1)
        setUploadError(`ファイルサイズが大きすぎます（${actualMb}MB）。10MB以下の画像をアップロードしてください`)
        resetSelection()
        return
      }

      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = (e) => setPreview(e.target?.result as string)
        reader.readAsDataURL(file)
      }

      setFilename(file.name)
      setIsUploading(true)

      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('document_type', fieldKey)
        formData.append('provisional_reservation_token', String(provisionalReservationToken))

        const csrfToken =
          document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''

        const response = await fetch('/online_clinic/reservations/upload_document', {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrfToken },
          body: formData,
        })

        if (!response.ok) {
          let errorMessage = `アップロードに失敗しました（サーバーエラー: ${response.status}）`
          try {
            const errorData = await response.json()
            if (errorData?.error) errorMessage = errorData.error
          } catch {
            // JSONでないレスポンス（プロキシの502/504など）はそのまま既定メッセージを使う
          }
          // eslint-disable-next-line no-console
          console.error('[InterviewForm.FileUpload] upload failed', {
            status: response.status,
            fieldKey,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          })
          setUploadError(errorMessage)
          resetSelection()
          return
        }

        const data = await response.json()

        if (data.success) {
          onChange(fieldKey, data.url)
        } else {
          setUploadError(data.error || 'アップロードに失敗しました')
          resetSelection()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[InterviewForm.FileUpload] upload exception', error, {
          fieldKey,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        })
        const message =
          error instanceof TypeError
            ? 'ネットワークエラーが発生しました。通信状態をご確認の上、再度お試しください'
            : `アップロードに失敗しました（${error instanceof Error ? error.message : '不明なエラー'}）`
        setUploadError(message)
        resetSelection()
      } finally {
        setIsUploading(false)
      }
    },
    [fieldKey, provisionalReservationToken, onChange]
  )

  const handleRemove = useCallback(() => {
    setPreview(null)
    setFilename(null)
    setUploadError(null)
    onChange(fieldKey, '')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [fieldKey, onChange])

  const handleClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // pills_prescription_image の保存先アップローダは PDF 非対応のため画像のみ許可。それ以外は PDF も許可
  const acceptTypes =
    fieldKey === 'pills_prescription_image' ? 'image/*,.heic,.heif' : 'image/*,.pdf,.heic,.heif'

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptTypes}
        onChange={handleFileSelect}
        className="hidden"
      />

      {!value && !isUploading && (
        <>
          <button
            type="button"
            onClick={handleClick}
            className={`button-secondary mt-1 ${hasError || uploadError ? 'border-red-400' : ''}`}
          >
            写真を撮ってアップロード
          </button>
          <p className="mt-2 text-xs text-[#7f8388]">JPG, PNG, HEIC（10MB以下）</p>
        </>
      )}

      {uploadError && (
        <p className="mt-2 text-sm text-red-600">{uploadError}</p>
      )}

      {isUploading && (
        <div className="mt-2 flex items-center justify-center rounded-lg bg-[#eef3f7] p-6">
          <svg
            className="animate-spin h-6 w-6 text-cyan-500 mr-2"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-gray-600">アップロード中...</span>
        </div>
      )}

      {value && !isUploading && (
        <div className="mt-2 relative">
          <div className="relative overflow-hidden rounded-lg border border-[#d8dde3] bg-[#f3f5f7]">
            {preview && preview !== value ? (
              <img src={preview} alt="プレビュー" className="w-full h-48 object-contain" />
            ) : value ? (
              <img src={value} alt="プレビュー" className="w-full h-48 object-contain" />
            ) : null}
          </div>

          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm text-green-600 flex items-center">
              <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              アップロード完了
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClick}
                className="text-sm text-[#2196f3] hover:underline"
              >
                変更
              </button>
              <button
                type="button"
                onClick={handleRemove}
                className="text-sm text-red-600 hover:underline"
              >
                削除
              </button>
            </div>
          </div>
          {filename && <p className="text-xs text-gray-500 mt-1">{filename}</p>}
        </div>
      )}
    </div>
  )
}

function renderFieldInput(
  field: InterviewField,
  values: Record<string, string | string[]>,
  onChange: (key: string, value: string | string[]) => void,
  hasError: boolean
) {
  const value = values[field.key]
  const baseInputClass = `${field.type === 'textarea' ? 'oc-textarea' : 'oc-input'} ${
    hasError ? '!border-red-400 focus:!border-red-400 focus:!ring-red-200' : ''
  }`

  switch (field.type) {
    case 'text':
      return (
        <input
          type="text"
          value={(value as string) || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          className={baseInputClass}
          placeholder={field.placeholder}
        />
      )

    case 'number':
      return (
        <div className="relative">
          <input
            type="number"
            value={(value as string) || ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            className={`${baseInputClass} ${UNIT_BY_FIELD_KEY[field.key] ? 'pr-16' : ''}`}
            step="any"
            placeholder={field.placeholder}
          />
          {UNIT_BY_FIELD_KEY[field.key] && (
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#515862]">
              {UNIT_BY_FIELD_KEY[field.key]}
            </span>
          )}
        </div>
      )

    case 'textarea':
      return (
        <textarea
          value={(value as string) || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          className={baseInputClass}
          rows={4}
          placeholder={field.placeholder}
        />
      )

    case 'date':
      return (
        <input
          type="date"
          value={(value as string) || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          className={baseInputClass}
        />
      )

    case 'radio':
      return (
        <div className="space-y-3">
          {field.options?.map((option) => (
            <label key={option.value} className="flex cursor-pointer items-center gap-3 text-[#2a3039]">
              <input
                type="radio"
                name={field.key}
                value={option.value}
                checked={value === option.value}
                onChange={(e) => onChange(field.key, e.target.value)}
                className="oc-radio"
              />
              <span className="text-base">{option.label}</span>
            </label>
          ))}
        </div>
      )

    case 'checkbox_group':
      return (
        <div className="space-y-3">
          {field.options?.map((option) => {
            const selectedValues = Array.isArray(value) ? value : []
            const isChecked = selectedValues.includes(option.value)

            return (
              <label key={option.value} className="flex cursor-pointer items-center gap-3 text-[#2a3039]">
                <input
                  type="checkbox"
                  value={option.value}
                  checked={isChecked}
                  onChange={(e) => {
                    if (e.target.checked) {
                      if (option.value === 'none') {
                        onChange(field.key, ['none'])
                      } else {
                        const newValues = selectedValues.filter((v) => v !== 'none')
                        onChange(field.key, [...newValues, option.value])
                      }
                    } else {
                      onChange(
                        field.key,
                        selectedValues.filter((v) => v !== option.value)
                      )
                    }
                  }}
                  className="oc-checkbox"
                />
                <span className="text-base">{option.label}</span>
              </label>
            )
          })}
        </div>
      )

    case 'checkbox':
      return (
        <label className="flex cursor-pointer items-center gap-3 rounded-lg bg-[#f0f0f0] p-4 text-[#2a3039]">
          <input
            type="checkbox"
            checked={value === 'true'}
            onChange={(e) => onChange(field.key, e.target.checked ? 'true' : 'false')}
            className="oc-checkbox"
          />
          <span className="text-base">{field.label}</span>
        </label>
      )

    default:
      return null
  }
}
